import "./registration-form.scss";

import { useCallback, useEffect } from "react";
import { WVUSForm } from "wvus-forms";

import { configureFormData, getUserDataFields } from "../../helpers/formDataHelpers";
import { delay } from "../../helpers/helpers";
import useSendAuthLinkError from "../../helpers/hooks/useSendAuthLinkError";
import { registrationRequest, sendAuthLink } from "../../services/requests";
import appSettings from "../../settings/appSettings";
import BackButton from "../buttons/BackButton";
import ProgressBar from "../progress-bar/ProgressBar";
import FormContent from "./FormContent";
import { useFormFlowContext } from "./FormFlowContext";

/**
 * RegistrationForm component handles the multi-step registration process.
 * It uses the WVUSForm HOC and integrates with the useFormFlowContext for form flow management.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.formMethods - Methods for form handling.
 * @param {Object} props.userSessionData - Data related to the user's session.
 *
 * @returns {JSX.Element} The rendered RegistrationForm component.
 */
const RegistrationForm = WVUSForm(({ formMethods }) => {
  const {
    currentPage,
    currentView,
    formError,
    noAccountsExperience,
    pageBackward,
    pageForward,
    readyToImplicitlySubmit,
    setFormError,
    setFormSuccess,
    setSubmittingMessage,
    setView,
    userData
  } = useFormFlowContext();
  const { onSendAuthLinkErrorPageForward } = useSendAuthLinkError();

  const { welcomeConfig, surveyConfig, badges, widgetId, widgetType, wvusLogger } = appSettings;

  const { surveyQuestions = [] } = surveyConfig;

  const { getFormState } = formMethods;
  const formState = getFormState();

  const onFormError = useCallback(
    (err) => {
      const statusCode = err?.resp?.status;
      const errorMessage = err?.message;

      const knownErrors = ["read_supporter", "create_auth0_account", "create_member"];
      const errorReason = knownErrors.reduce((currentReason, knownErrorStr) => {
        if (!currentReason && errorMessage.includes(knownErrorStr)) {
          return `${knownErrorStr}_error_`;
        }

        return currentReason;
      }, "");

      if ([401, 403].includes(statusCode)) {
        setFormError({
          errorKey: "registerUser",
          errorType: "general",
          errorCode: `${errorReason}${statusCode}`,
          saveError: false
        });
      } else {
        setFormError({
          errorKey: "registerUser",
          errorType: "general",
          errorCode: `${errorReason}${statusCode || "error"}`,
          saveError: true
        });
      }

      wvusLogger.log({ errorMessage, statusCode }, wvusLogger.getLogLevelNames().ERROR);
      setFormSuccess(false);
    },
    [setFormError, setFormSuccess, wvusLogger]
  );

  const handleSubmit = useCallback(() => {
    if (formError) return;

    setView("submitting");
    const data = {
      fields: formState.fields,
      supporterNumber: userData?.supporterNumber,
      widget: { id: widgetId, type: widgetType },
      userDataFields: getUserDataFields(welcomeConfig),
      surveyQuestions,
      badges
    };
    const formData = configureFormData(data);

    registrationRequest(formData)
      .then(() => setFormSuccess(true))
      .catch(onFormError)
      .then(async () => {
        if (noAccountsExperience) {
          await sendAuthLink(userData.email)
            .then(() => delay(1500))
            .then(() => setFormSuccess(false))
            .then(() => pageForward())
            .catch(
              onSendAuthLinkErrorPageForward(
                "Error sending one-time login email after account creation."
              )
            );
        } else {
          pageForward();
        }
      });
  }, [
    formError,
    setView,
    formState?.fields,
    userData?.supporterNumber,
    userData?.email,
    widgetId,
    widgetType,
    welcomeConfig,
    surveyQuestions,
    badges,
    onFormError,
    setFormSuccess,
    noAccountsExperience,
    onSendAuthLinkErrorPageForward,
    pageForward
  ]);

  useEffect(() => {
    if (readyToImplicitlySubmit) {
      setSubmittingMessage("Setting up your account...");
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const showBackButton = [
    "badges",
    "survey",
    "wrong_info",
    "password",
    "login_issues__signin",
    "login_issues__one_time_login"
  ].includes(currentView);

  return (
    <div
      className="registration-form"
      style={{ backgroundImage: `url(${appSettings.backgroundImageUrl})` }}>
      <ProgressBar />
      <div className={`form-container display-grid gap-base${!showBackButton ? " ceiling" : ""}`}>
        <BackButton handleClick={pageBackward} visible={showBackButton} />
        <FormContent formMethods={formMethods} handleSubmit={handleSubmit} />
      </div>
    </div>
  );
});

export default RegistrationForm;
